import {
    GoogleMap,
    Libraries,
    LoadScript,
    Marker,
    OverlayView,
    StreetViewPanorama,
} from '@react-google-maps/api';
import { ENV } from '../../infrastructure/config/CONSTANTS';
import { Button, Input } from 'antd';
import { commonHelpers } from 'utilities/helpers';
import MapMarker from '../../assets/icons/map_marker.svg';
import { useEffect, useRef, useState } from 'react';

export interface StreetViewProps {
    containerStyle: {
        height: string;
        width: string;
    };
    center: {
        lat: number;
        lng: number;
    };
    streetViewVisible?: boolean;
    zoom?: number;
    address?: string;
    propertyName?: string;
}

const lib: Libraries = ['places'];

export const MapStreetView = ({
    containerStyle,
    center,
    streetViewVisible = true,
    zoom = 10,
}: StreetViewProps) => {
    const panoRef = useRef<google.maps.StreetViewPanorama | null>(null);
    const [panoId, setPanoId] = useState<string | null>(null);

    // 屋外パノラマのみ取得
    useEffect(() => {
        if (!streetViewVisible) return;
        if (!window.google?.maps) return;

        const sv = new google.maps.StreetViewService();

        sv.getPanorama(
            {
                location: center,
                radius: 50,
                source: google.maps.StreetViewSource.OUTDOOR,
            },
            (data, status) => {
                if (
                    status === google.maps.StreetViewStatus.OK &&
                    data?.location?.pano
                ) {
                    setPanoId(data.location.pano);
                } else {
                    setPanoId(null);
                }
            }
        );
    }, [center.lat, center.lng, streetViewVisible]);

    // panoId がある場合のみ表示
    useEffect(() => {
        if (!panoRef.current) return;

        if (panoId && streetViewVisible) {
            panoRef.current.setPano(panoId);
            panoRef.current.setPov({
                heading: 34,
                pitch: 0, // 屋内吸い込み防止
            });
            panoRef.current.setZoom(0);
            panoRef.current.setVisible(true);
        } else {
            panoRef.current.setVisible(false);
        }
    }, [panoId, streetViewVisible]);

    return (
        <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={zoom}
            options={{
                disableDefaultUI: true,
                tilt: 0,
                maxZoom: 17,
                mapTypeId: 'roadmap',
            }}
        >
            <StreetViewPanorama
                onLoad={(pano) => (panoRef.current = pano)}
                options={{
                    position: center, // 初期位置（panoIdで上書き）
                    visible: false,   // 初期は非表示
                    enableCloseButton: false,
                    disableDefaultUI: true,
                    fullscreenControl: true,
                }}
            />
        </GoogleMap>
    );
};

export const StreetView = (props: StreetViewProps) => {
    return (
        <LoadScript googleMapsApiKey={ENV.GOOGLE_MAP_API_KEY} libraries={lib}>
            <MapStreetView {...props} />
        </LoadScript>
    );
};

export const MapView = (props: StreetViewProps) => {
    return (
        <LoadScript googleMapsApiKey={ENV.GOOGLE_MAP_API_KEY} libraries={lib}>
            <GoogleMap
                mapContainerStyle={props.containerStyle}
                center={props.center}
                zoom={props.zoom}
                options={{
                    disableDefaultUI: true,
                    disableDoubleClickZoom: true,
                    clickableIcons: false,
                    tilt: 0,
                    maxZoom: 17,
                    mapTypeId: 'roadmap',
                }}
            >
                <Marker position={props.center} icon={MapMarker}>
                    {props.propertyName && (
                        <OverlayView
                            position={props.center}
                            mapPaneName={OverlayView.FLOAT_PANE}
                        >
                            <div className="popup-container">
                                <div className="popup-bubble-anchor">
                                    <div id="content" className="popup-bubble">
                                        {props.propertyName}
                                    </div>
                                </div>
                            </div>
                        </OverlayView>
                    )}
                </Marker>

                <div className="header-map">
                    <Input
                        className="input-search-map"
                        disabled
                        value={props.address}
                    />
                    <Button className="map-button">
                        {props.propertyName}
                    </Button>
                    <Button
                        className="top-popup-container map-button"
                        onClick={() =>
                            commonHelpers.openGoogleMap(
                                props.center.lat,
                                props.center.lng
                            )
                        }
                    >
                        地図アプリで開く
                    </Button>
                </div>
            </GoogleMap>
        </LoadScript>
    );
};
